import React, {useEffect} from 'react'
import {Box, BoxProps, HStack, Skeleton, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import {ProductSkeleton, ProductTile, Tabs} from '_uiComponents'
import {FullProductType, ProductType} from '_types'
import {useAnalytics, useEinstein, useViewport} from '_customHooks'
import {useIntl} from 'react-intl'
import {useDNT, useProducts} from '@salesforce/commerce-sdk-react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {FreeMode, Mousewheel} from 'swiper/modules'

interface ProductRecommendationsProps {
    product: FullProductType
}

const PDP_RECOMMENDER_ID = 'pdp-relatives'
const MIN_PRODUCTS = 3

/**
 * @name Stickybar
 *
 * @component
 *
 * @description
 * Renders a menu and a dark overlay.
 *
 * @remarks
 * Dark Overlay component ensures that on tablet the correct background under the sidebar is rendered.
 *
 * @returns {React.FC} - The rendered sidebar component.
 */
const ProductRecommendations: React.FC<ProductRecommendationsProps> = ({product}: ProductRecommendationsProps) => {
    const {formatMessage} = useIntl()
    const {getRecommendations, recommendations: einsteinRecommendations, sendViewReco} = useEinstein()
    const {analyticsEventPdpCarouselSwitch} = useAnalytics()
    const {effectiveDnt} = useDNT()
    const {isMobile, isTablet} = useViewport()

    // Master SKU used as eventLabel for the carousel switch event (manuale BitBang PDP PWA §3.1.7).
    const analyticsSku = product.c_analytics?.productId ?? product.id ?? ''

    const styles = useMultiStyleConfig('ProductRecommendations', {}) as Record<string, BoxProps>

    const COMMERCE_TAB_LABEL = formatMessage({id: 'label.pdp.recs.look', defaultMessage: 'Complete the look'})
    const EINSTEIN_TAB_LABEL = formatMessage({id: 'label.pdp.recs.einstein', defaultMessage: 'You might like'})

    // Build the id lists once so `ids` and `enabled` stay in sync: an empty string ('') is NOT a
    // valid `ids` value and would trigger "Missing required query parameter: ids", so we gate the
    // query on a non-empty id string (and, for commerce, the minimum-count business rule).
    const einsteinIds = einsteinRecommendations?.recs?.map?.((rec) => rec.id).join(',') || ''
    const commerceIds = product?.recommendations?.map?.((rec) => rec.recommendedItemId).join(',') || ''
    const hasEnoughCommerceRecs = (product.recommendations?.length ?? 0) >= MIN_PRODUCTS

    const einsteinEnabled = Boolean(einsteinIds)
    const commerceEnabled = Boolean(commerceIds) && hasEnoughCommerceRecs

    const {data: einsteinRecommendedProducts, isInitialLoading: isLoadingEinsteinRecommendedProducts} = useProducts(
        {
            parameters: {
                ids: einsteinIds,
                allImages: false,
                expand: ['availability'],
                c_productTileHeader: true,
            },
        },
        {
            enabled: einsteinEnabled,
        },
    )

    const {data: commerceRecommendedProducts, isInitialLoading: isLoadingCommerceRecommendedProducts} = useProducts(
        {
            parameters: {
                ids: commerceIds,
                allImages: false,
                expand: ['availability'],
                c_productTileHeader: true,
            },
        },
        {
            enabled: commerceEnabled,
        },
    )

    // Reuse the canonical category list generated by the product service, matching the
    // value PLP receives from category.c_analyticsList. Only its context prefix differs.
    const commerceAnalyticsList = product.c_analytics?.list?.replace(/^category-/, 'relatives-') ?? ''
    const einsteinAnalyticsList = product.c_analytics?.list?.replace(/^category-/, 'einstein-') ?? ''

    const renderCarousel = (products: Array<ProductType> | undefined, isEinstein: boolean) => {
        if (!products) return <></>

        // Slides shown per breakpoint (Figma): mobile 1 (centered, ~1.1 peek), tablet 2 full + a small
        // peek of the 3rd (node 60805-36907 → 2.2, so the two main tiles are a bit wider and the 3rd shows
        // only a narrow strip), desktop 4 (~4.2 peek). Tablet is a touch device, so it shares mobile's
        // native-touch interaction (no desktop trackpad free-mode); only the slide count and spacing differ.
        const isTouch = isMobile || isTablet
        const slidesPerView = isMobile ? 1.1 : isTablet ? 2.2 : 4.2
        const spaceBetween = isTouch ? 10 : 20

        return (
            <Swiper
                direction="horizontal"
                className="mySwiper cc-pdp-recommended-products"
                slidesPerView={slidesPerView}
                spaceBetween={spaceBetween}
                centeredSlides={isMobile}
                mousewheel={
                    isTouch
                        ? {
                              enabled: true,
                              releaseOnEdges: false,
                              thresholdDelta: 25,
                              thresholdTime: 300,
                              forceToAxis: true,
                          }
                        : {
                              // Desktop trackpad tuning. Two problems were being solved:
                              // 1) The old wheel thresholds (25px / 300ms, tuned for discrete wheel ticks) discarded
                              //    most of the trackpad's small continuous deltas → scrolling felt heavy.
                              // 2) macOS momentum: after a trackpad flick the OS keeps emitting decaying wheel events,
                              //    and Swiper's default slide-by-slide snap turned each into "one more slide", so the
                              //    carousel drifted an extra slide on its own.
                              // Pairing the wheel with `freeMode` below makes it track the gesture pixel-by-pixel
                              // (no per-tick slide snap), which removes the auto-advance. `forceToAxis: true` keeps a
                              // plain vertical mouse wheel scrolling the PAGE (only horizontal-axis delta drives it).
                              enabled: true,
                              releaseOnEdges: false,
                              forceToAxis: true,
                          }
                }
                // Desktop only: free (pixel-based) scrolling so the trackpad drives the carousel continuously
                // instead of snapping a whole slide per wheel tick. Momentum is kept ON (with a reduced ratio)
                // so the carousel decelerates smoothly at the end of a gesture instead of stopping abruptly —
                // disabling momentum made the scroll feel jerky. The reduced ratio keeps the coasting short so
                // it doesn't drift an extra slide. Mobile keeps its native touch behaviour (no freeMode).
                freeMode={isTouch ? false : {enabled: true, sticky: false, momentum: true, momentumRatio: 0.4, momentumBounce: false}}
                modules={isTouch ? [Mousewheel] : [Mousewheel, FreeMode]}
            >
                {products?.map((product, index) => (
                    <SwiperSlide key={`${product.id ?? 'reco'}-${index}`}>
                        <ProductTile
                            product={product}
                            idx={index}
                            hasLazy={true}
                            showCarousel={false}
                            showHover={false}
                            listingLayout="carousel" // shows QuickAddButton on mobile and removes lateral padding
                            analyticsList={isEinstein ? einsteinAnalyticsList : commerceAnalyticsList}
                            addToCartOrigin={isEinstein ? 'einstein' : 'recommendation'}
                            productTileContext={
                                isEinstein
                                    ? {
                                          type: 'recommendation',
                                          recommenderName: einsteinRecommendations?.recommenderName,
                                          recoUUID: einsteinRecommendations?.recoUUID,
                                      }
                                    : undefined
                            }
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        )
    }

    // Fixed English analytics names for the carousel-switch event (manuale BitBang PDP PWA §3.1.7),
    // decoupled from the localized tab labels: Einstein "Potrebbe piacerti" → 'You may like',
    // Commerce "Completa il look" → 'Complete the look'.
    const EINSTEIN_TAB_ANALYTICS_NAME = 'You may like'
    const COMMERCE_TAB_ANALYTICS_NAME = 'Complete the look'

    const renderTabs = (tabs: Array<{label: string; analyticsName: string; content: React.JSX.Element}>) => {
        return (
            <Box {...styles.tabsContainer}>
                <Tabs
                    tabs={tabs}
                    variant="recommendations"
                    activeIndex={0}
                    onTabChange={(index) => analyticsEventPdpCarouselSwitch({tabName: tabs[index]?.analyticsName ?? '', sku: analyticsSku})}
                    enableDefaultPanels
                ></Tabs>
            </Box>
        )
    }

    const einsteinTab =
        einsteinRecommendedProducts && einsteinRecommendedProducts.data?.length > 0
            ? [{label: EINSTEIN_TAB_LABEL, analyticsName: EINSTEIN_TAB_ANALYTICS_NAME, content: renderCarousel(einsteinRecommendedProducts?.data, true)}]
            : []
    const commerceTab =
        commerceRecommendedProducts && commerceRecommendedProducts.data?.length >= MIN_PRODUCTS
            ? [{label: COMMERCE_TAB_LABEL, analyticsName: COMMERCE_TAB_ANALYTICS_NAME, content: renderCarousel(commerceRecommendedProducts?.data, false)}]
            : []

    const tabs = [...einsteinTab, ...commerceTab]

    useEffect(() => {
        // Always fetch Einstein recs (generic without consent, personalized with it). The
        // effectiveDnt dependency triggers a re-fetch when the consent state changes, so the
        // results switch between generic and personalized accordingly.
        void getRecommendations(PDP_RECOMMENDER_ID, [product])
    }, [effectiveDnt])

    useEffect(() => {
        // send viewReco Einstein activity when recs are ready.
        const {recommenderName, recoUUID: __recoUUID, recs: products} = einsteinRecommendations
        void sendViewReco({recommenderName, __recoUUID}, products)
    }, [einsteinRecommendations])

    if (isLoadingEinsteinRecommendedProducts || isLoadingCommerceRecommendedProducts) {
        return (
            <VStack w={'100%'} pl={'60px'} gap={'50px'} overflow={'hidden'}>
                <HStack w={'100%'} gap={'24px'}>
                    <Skeleton h={'45px'} w={'130px'} />
                    <Skeleton h={'45px'} w={'130px'} />
                </HStack>
                <HStack w={'120%'} ml={0} mr={'auto'}>
                    {Array.from({length: 5}, (_, index) => (
                        <ProductSkeleton key={`skeleton-${index}`} />
                    ))}
                </HStack>
            </VStack>
        )
    }

    return (
        <VStack {...styles.wrapper}>
            {isMobile ? (
                // Da design: su mobile l'ordine è invertito rispetto a desktop, "Completa il look" prima di "Potrebbe piacerti".
                <>
                    {renderTabs(commerceTab)}
                    {renderTabs(einsteinTab)}
                </>
            ) : (
                <>{renderTabs(tabs)}</>
            )}
        </VStack>
    )
}

ProductRecommendations.displayName = 'ProductRecommendations'

export default ProductRecommendations
